import { useEffect } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import { logUser } from 'src/lib/dynatrace';

interface UseDynatraceLoaderOptions {
  url?: string;
  readyEventName?: string;
}

export const DYNATRACE_READY_EVENT = 'dynatrace:ready';

/**
 * Ensures the Dynatrace script is injected exactly once and emits a window event when ready.
 * Intended to be called once near the app root.
 */
export const useDynatraceLoader = (options: UseDynatraceLoaderOptions = {}): void => {
  useEffect(() => {
    const dynatraceUrl = options.url ?? import.meta.env.VITE_DYNATRACE_URL;
    const readyEventName = options.readyEventName ?? DYNATRACE_READY_EVENT;

    if (!dynatraceUrl) return () => {};

    const dispatchReady = () => {
      window.dispatchEvent(new Event(readyEventName));
    };

    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${dynatraceUrl}"]`
    );

    // If the script tag already exists, just wait for it (or fire immediately if dtrum is already present).
    if (existingScript) {
      if (window.dtrum) {
        dispatchReady();
        return () => {};
      }

      existingScript.addEventListener('load', dispatchReady);
      return () => {
        existingScript.removeEventListener('load', dispatchReady);
      };
    }

    // Otherwise inject it.
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = dynatraceUrl;
    script.crossOrigin = 'anonymous';

    script.addEventListener('load', dispatchReady);
    script.addEventListener('error', () => {
      // Keep it quiet-ish; downstream code should guard on window.dtrum
      console.warn('Dynatrace script failed to load');
    });

    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', dispatchReady);
    };
  }, [options.readyEventName, options.url]);
};

interface UseDynatraceIdentifyUserOptions {
  readyEventName?: string;
}

/**
 * Identifies the current user in Dynatrace:
 * - when the email becomes available / changes
 * - when the window regains focus (covers new Dynatrace session after inactivity)
 */
export const useDynatraceIdentifyUser = (
  email: string | undefined,
  options: UseDynatraceIdentifyUserOptions = {}
): void => {
  const debouncedIdentify = useDebounceCallback(() => {
    if (!email || !window.dtrum) return;
    logUser(email);
  }, 1000); // 1 seconds debounce to avoid rapid refocus issues

  useEffect(() => {
    if (!email) return () => {};

    const readyEventName = options.readyEventName ?? DYNATRACE_READY_EVENT;

    const identify = () => {
      if (!window.dtrum) return;
      logUser(email);
    };

    // Identify as soon as possible on email change
    identify();

    // If Dynatrace loads after the app, identify once it's ready
    window.addEventListener(readyEventName, identify);

    // Re-identify when user returns after inactivity (new Dynatrace session)
    window.addEventListener('focus', debouncedIdentify);

    return () => {
      window.removeEventListener(readyEventName, identify);
      window.removeEventListener('focus', debouncedIdentify);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, options.readyEventName]);
};
